import { IListUser } from 'src/redux/slices/users';

export type IChequeTableFilterValue = string | Date | boolean | null;

export type IChequeTableFilters = {
  generalSearch: string;
  accountNumber: string | null;
  receivedVia: string | null;
  chequeDate: Date | null;
  bank: string | null;
  startDate: Date | null;
  endDate: Date | null;
  receivedDate: Date | null;
  // In Hand tab only: cheque date on or before this day ("passing by").
  chequeDateUpTo: Date | null;
  type: string | null;
  paidDate: Date | null;
  // Banking tab only: the day the cheque was banked.
  bankedDate: Date | null;
  isFactored: boolean | null;
  factoringStatus: string | null;
};

export type IChequeTableSort = {
  sortBy: string;
  order: 'asc' | 'desc';
};

export enum ChequeStatus {
  IN_HAND = 'In Hand',
  HOLDING = 'Holding',
  BANKING = 'Banking',
  FACTORING = 'Factoring',
  FACTORED = 'Factored',
  PAID = 'Paid',
  RETURN = 'Return',
  SETTLED = 'Settled',
  HANDOVER = 'Handover',
  RECOVERING = 'Recovering',
  RECOVERED = 'Recovered',
}

// Independent of ChequeStatus — tracks a factored cheque's lifecycle with LOLC PLC.
export enum FactoringStatus {
  HANDED_OVER = 'Handed Over',
  ADVANCE_RECEIVED = 'Advance Received',
  CLEARED = 'Cleared',
  RETURNED = 'Returned',
  SETTLED_WITH_LOLC = 'Settled With LOLC',
}

export enum ChequeCustomerType {
  SALE = 'sale',
  WHOLESALE = 'wholesale',
}

export const CHEQUE_STATUS_OPTIONS = [
  { value: ChequeStatus.IN_HAND, label: 'In Hand' },
  { value: ChequeStatus.HOLDING, label: 'Holding' },
  { value: ChequeStatus.BANKING, label: 'Banking' },
  { value: ChequeStatus.FACTORING, label: 'Factoring' },
  { value: ChequeStatus.FACTORED, label: 'Factored' },
  { value: ChequeStatus.PAID, label: 'Paid' },
  { value: ChequeStatus.RETURN, label: 'Return' },
  { value: ChequeStatus.SETTLED, label: 'Settled' },
  { value: ChequeStatus.HANDOVER, label: 'Handover' },
  { value: ChequeStatus.RECOVERING, label: 'Recovering' },
  { value: ChequeStatus.RECOVERED, label: 'Recovered' },
];

export interface IChequeListItem {
  _id: string;
  customer: string;
  accountNumber: number | null;
  branchCode: string | null;
  receivedVia: string;
  holder: string;
  chequeNumber: string;
  chequeDate: string;
  receivedDate: string;
  amountInCents: number;
  bank: string;
  status: ChequeStatus;
  createdAt: string;
  paidHistory?: IChequeItemHistory[];
  // Set by the list endpoint on the Paid tab (earliest Paid history entry).
  paidDate?: string | null;
  // Set once the cheque is moved to Banking: the banking date.
  bankedAt?: string | null;
  payerAccountNumber: string | null;
  payerName: string | null;
  isFactored: boolean;
  factoringStatus: FactoringStatus | null;
  factoringBatch: string | null;
  factoringReturnedAt: string | null;
  factoringSettlementDueAt: string | null;
  factoringClearedDate: string | null;
  factoringSettledDate: string | null;
}

export interface IChequeItemHistory {
  _id: string;
  holder: string;
  date: string;
  status: ChequeStatus;
  createdBy: IListUser;
  updatedBy: IListUser;
  createdAt: string;
  updatedAt: string;
}

export interface IChequeItem {
  _id: string;
  customer: string;
  accountNumber: number | null;
  branchCode: string | null;
  invoice: string | null;
  receivedVia: string;
  chequeNumber: string;
  chequeDate: string;
  receivedDate: string;
  amountInCents: number;
  bank: string;
  holder: string;
  status: ChequeStatus;
  type: ChequeCustomerType;
  history: IChequeItemHistory[];
  createdBy: IListUser;
  updatedBy: IListUser;
  createdAt: string;
  updatedAt: string;
  payerAccountNumber: string | null;
  payerName: string | null;
  note?: string | null;
  // Set while held at the issuer's request (and through recovery from it).
  heldAt?: string | null;
  bankedAt?: string | null;
  isFactored: boolean;
  factoringStatus: FactoringStatus | null;
  factoringBatch: string | null;
  factoringReturnedAt: string | null;
  factoringSettlementDueAt: string | null;
  factoringClearedDate: string | null;
  factoringSettledDate: string | null;
}

export interface IChequeReceived {
  receivedVia: string;
}

export interface IChequePayment {
  _id: string;
  amountInCents: number;
  paymentDate: string;
  note?: string;
  cheque: string;
  recoveryHistory?: string;
  paidDirectToSupplier?: boolean;
  createdBy: IListUser;
  updatedBy: IListUser;
  createdAt: string;
  updatedAt: string;
}

export interface IChequePaymentTotal {
  totalPaid: number;
}

export type ChequeTimelineEventType = 'STATUS' | 'RECOVERY' | 'SETTLEMENT' | 'DIRECT_TO_SUPPLIER';

export interface IChequeTimelineEvent {
  id: string;
  type: ChequeTimelineEventType;
  status?: ChequeStatus;
  holder?: string;
  date: string;
  at: string;
  amountInCents?: number;
  note?: string;
  by?: { _id: string; firstName?: string; lastName?: string };
  balances: {
    recoveredInCents: number;
    recoveryOutstandingInCents: number;
    settledInCents: number;
    settlementOutstandingInCents: number;
    companyNetPositionInCents: number;
  };
  amountNotRecorded?: boolean;
}

export interface IChequeTimeline {
  amountInCents: number;
  events: IChequeTimelineEvent[];
}

export enum ChequeSettlementSource {
  COMPANY = 'Company',
  DEBTOR_DIRECT = 'Debtor Direct',
}

export interface IChequeSettlement {
  _id: string;
  amountInCents: number;
  settlementDate: string;
  note?: string;
  cheque: string;
  source: ChequeSettlementSource;
  sourcePayment?: string;
  createdBy: IListUser;
  updatedBy: IListUser;
  createdAt: string;
  updatedAt: string;
}

export interface IChequeSettlementSummary {
  amountInCents: number;
  recoveredInCents: number;
  recoveryOutstandingInCents: number;
  settledInCents: number;
  settledByCompanyInCents: number;
  settledByDebtorDirectInCents: number;
  settlementOutstandingInCents: number;
  supplierSettlementStatus: 'Unsettled' | 'Partially Settled' | 'Settled';
  receivedByCompanyInCents: number;
  companyNetPositionInCents: number;
  isClosed: boolean;
}
